/**
 * ประวัติของโครงการ — FB-2026-0009 (tirawat โครงการ #682, 3 พ.ย. 2026)
 * "อยากให้มีประวัติว่าเคยขอเปลี่ยนอุปกรณ์เสริมอะไรไปแล้วบ้าง และเคยเปลี่ยนเครื่องหรือซ่อมอะไรมาแล้วบ้าง
 *  เพื่อเอาไว้เป็นข้อมูลเวลาตอบคำถามของบริษัท รปภ"
 *
 * - ประวัติการเปลี่ยนเครื่อง/ซิม = GET /device-replacements/all-history?site_id= (device_replacements.view)
 * - ประวัติเบิก/เปลี่ยนอุปกรณ์เสริม = GET /stock-issues/?site_id= (stock_issues.view) — ใบที่ผูกสัญญาของโครงการ
 *   (stock_issues ไม่มี site_id ของตัวเอง · ใบเก่าที่ไม่ได้ผูกสัญญาจะไม่โผล่ — ใบที่เปิดจากเคสซ่อมผูกให้แล้ว FB-2026-0008)
 * - ประวัติซ่อม = การ์ด "เคสซ่อมบำรุงของโครงการนี้" เดิม (ไม่ซ้ำที่นี่)
 *
 * gate สิทธิ์ของโมดูลต้นทางแยกการ์ด — ไม่มีสิทธิ์ = ไม่ render และไม่ยิง API (G-12)
 * เรียงใหม่→เก่า (backend) · แบ่งหน้า PAGE_SIZE ต่อหน้า · ลิงก์ไปเอกสาร (gate สิทธิ์ปลายทาง)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { RiArrowLeftSLine, RiArrowRightSLine, RiExchangeLine, RiInboxUnarchiveLine, RiLoader4Line } from 'react-icons/ri';
import { DetailSection } from '../detail';
import { usePermission } from '../../hooks/usePermission';
import deviceReplacementService, {
  AllReplacementHistoryItem,
  DEVICE_CONDITIONS,
  REPLACEMENT_TYPES,
  SIM_CONDITIONS,
} from '../../services/deviceReplacementService';
import stockIssueService, { StockIssueRow } from '../../services/stockIssueService';
import { stockIssueDetailUrl } from '../../utils/stockIssueLinks';
import { formatDate, formatDateTimeFull } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

export const SITE_HISTORY_PAGE_SIZE = 10;

const conditionLabel = (v?: string | null): string | null =>
  v ? ([...DEVICE_CONDITIONS, ...SIM_CONDITIONS].find((c) => c.value === v)?.label ?? v) : null;

interface PagedShellProps {
  title: string;
  icon: React.ReactNode;
  testId: string;
  total: number;
  page: number;
  setPage: (p: number) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  emptyText: string;
  children: React.ReactNode;
}

const PagedShell: React.FC<PagedShellProps> = ({
  title, icon, testId, total, page, setPage, isLoading, isFetching, isError, error, refetch, emptyText, children,
}) => {
  const pages = Math.max(1, Math.ceil(total / SITE_HISTORY_PAGE_SIZE));
  return (
    <DetailSection
      title={total > 0 ? `${title} (${total.toLocaleString()})` : title}
      icon={icon}
      padded={false}
      testId={testId}
      description="เรียงจากล่าสุด"
      actions={isFetching && !isLoading ? <RiLoader4Line className="h-4 w-4 animate-spin text-secondary-400" /> : undefined}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-secondary-500">
          <RiLoader4Line className="h-5 w-5 animate-spin mr-2 text-primary-500" />
          กำลังโหลด...
        </div>
      ) : isError ? (
        <div className="px-4 py-4 sm:px-6 text-sm text-danger-700">
          {getApiErrorMessage(error, 'โหลดข้อมูลไม่สำเร็จ')}{' '}
          <button type="button" onClick={() => refetch()} className="underline">ลองอีกครั้ง</button>
        </div>
      ) : total === 0 ? (
        <p className="px-4 py-6 sm:px-6 text-sm text-secondary-500 text-center">{emptyText}</p>
      ) : (
        <>
          <ul className="divide-y divide-secondary-100">{children}</ul>
          {pages > 1 && (
            <div className="flex items-center justify-between gap-2 px-4 py-3 sm:px-6 border-t border-secondary-100 text-sm">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center min-h-[44px] sm:min-h-[32px] px-2 text-primary-600 disabled:text-secondary-300 disabled:cursor-not-allowed"
              >
                <RiArrowLeftSLine className="h-4 w-4" /> ใหม่กว่า
              </button>
              <span className="text-secondary-500" data-testid={`${testId}-page`}>หน้า {page}/{pages}</span>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={page >= pages}
                className="inline-flex items-center min-h-[44px] sm:min-h-[32px] px-2 text-primary-600 disabled:text-secondary-300 disabled:cursor-not-allowed"
              >
                เก่ากว่า <RiArrowRightSLine className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </DetailSection>
  );
};

interface SiteHistoryProps {
  siteId: number;
  /** state ที่ส่งไปกับลิงก์ (กลับมาหน้าโครงการได้) */
  hereState?: unknown;
}

/** ประวัติการเปลี่ยนเครื่อง/ซิมของรายการเช่าในโครงการ: วันที่ · เครื่องเก่า→ใหม่ · ซิม · เหตุผล · เคสซ่อมที่อ้าง */
export const SiteReplacementHistoryCard: React.FC<SiteHistoryProps> = ({ siteId, hereState }) => {
  const { hasPermission } = usePermission();
  const allowed = hasPermission('device_replacements.view');
  const canMaint = hasPermission('maintenance.view');
  const canRentals = hasPermission('rentals.view');
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['site-related', siteId, 'replacement-history', page],
    queryFn: () => deviceReplacementService.getAllReplacementHistory({ site_id: siteId, page, limit: SITE_HISTORY_PAGE_SIZE }),
    enabled: allowed,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
  if (!allowed) return null;
  const items: AllReplacementHistoryItem[] = q.data?.items ?? [];
  return (
    <PagedShell
      title="ประวัติการเปลี่ยนเครื่อง/ซิมของโครงการนี้"
      icon={<RiExchangeLine />}
      testId="site-history-replacements"
      total={q.data?.total ?? 0}
      page={page}
      setPage={setPage}
      isLoading={q.isLoading}
      isFetching={q.isFetching}
      isError={q.isError}
      error={q.error}
      refetch={() => q.refetch()}
      emptyText="โครงการนี้ยังไม่เคยเปลี่ยนเครื่องหรือซิม"
    >
      {items.map((r) => {
        const typeLabel = REPLACEMENT_TYPES.find((t) => t.value === r.replacement_type)?.label ?? r.replacement_type;
        const changesDevice = r.replacement_type !== 'SIM_ONLY';
        const changesSim = r.replacement_type !== 'DEVICE_ONLY';
        const cond = changesDevice ? conditionLabel(r.old_device_condition) : conditionLabel(r.old_sim_condition);
        return (
          <li key={r.id} className="px-4 py-3 sm:px-6 text-sm" data-testid={`site-history-replacement-${r.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <span className="font-medium text-secondary-900">{typeLabel}</span>
              <span className="text-xs text-secondary-500">{formatDateTimeFull(r.replacement_date)}</span>
            </div>
            {changesDevice && (
              <p className="text-secondary-700 [overflow-wrap:anywhere]">
                เครื่อง: <span className="font-mono">{r.old_device_code || '—'}</span> → <span className="font-mono">{r.new_device_code || '—'}</span>
                {r.device_type_name && <span className="text-secondary-500"> · {r.device_type_name}</span>}
              </p>
            )}
            {changesSim && (
              <p className="text-secondary-700">
                ซิม: <span className="font-mono">{r.old_sim_number || '—'}</span> → <span className="font-mono">{r.new_sim_number || '—'}</span>
              </p>
            )}
            <p className="text-xs text-secondary-600 [overflow-wrap:anywhere]">
              เหตุผล: {r.reason || '—'}
              {cond && <span> · ของเดิม: {cond}</span>}
            </p>
            <p className="text-xs flex flex-wrap gap-x-3">
              {r.maintenance_id && (canMaint ? (
                <Link to={`/maintenance/view/${r.maintenance_id}`} state={hereState} className="text-primary-600 hover:underline">
                  เคสซ่อม #{r.maintenance_id}
                </Link>
              ) : (
                <span className="text-secondary-500">เคสซ่อม #{r.maintenance_id}</span>
              ))}
              {canRentals ? (
                <Link to={`/rentals/view/${r.rental_id}`} state={hereState} className="text-primary-600 hover:underline font-mono">
                  {r.rental_code}
                </Link>
              ) : (
                <span className="text-secondary-500 font-mono">{r.rental_code}</span>
              )}
              {r.replaced_by_name && <span className="text-secondary-500">โดย {r.replaced_by_name}</span>}
            </p>
          </li>
        );
      })}
    </PagedShell>
  );
};

const issueItemsSummary = (row: StockIssueRow): string =>
  (row.items || [])
    .map((it) => {
      const label = it.display_label || [it.device_code, it.device_name].filter(Boolean).join(' ') || it.sim_phone || '—';
      return it.quantity > 1 ? `${label} ×${it.quantity}` : label;
    })
    .join(', ');

/** ประวัติเบิก/เปลี่ยนอุปกรณ์เสริม = ใบเบิก-ยืมที่ผูกสัญญาของโครงการ */
export const SiteAccessoryIssueHistoryCard: React.FC<SiteHistoryProps> = ({ siteId, hereState }) => {
  const { hasPermission } = usePermission();
  const allowed = hasPermission('stock_issues.view');
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['site-related', siteId, 'stock-issues', page],
    queryFn: () => stockIssueService.getIssues({ site_id: siteId, page, page_size: SITE_HISTORY_PAGE_SIZE }),
    enabled: allowed,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
  if (!allowed) return null;
  const items = q.data?.items ?? [];
  return (
    <PagedShell
      title="ประวัติเบิก/เปลี่ยนอุปกรณ์เสริมของโครงการนี้"
      icon={<RiInboxUnarchiveLine />}
      testId="site-history-stock-issues"
      total={q.data?.total ?? 0}
      page={page}
      setPage={setPage}
      isLoading={q.isLoading}
      isFetching={q.isFetching}
      isError={q.isError}
      error={q.error}
      refetch={() => q.refetch()}
      emptyText="ยังไม่มีใบเบิก-ยืมที่ผูกกับสัญญาของโครงการนี้"
    >
      {items.map((row) => (
        <li key={row.id} className="px-4 py-3 sm:px-6 text-sm" data-testid={`site-history-stock-issue-${row.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <Link to={stockIssueDetailUrl(row.id)} state={hereState} className="font-medium font-mono text-primary-600 hover:underline">
              {row.issue_number}
            </Link>
            <span className="text-xs text-secondary-500">
              {row.issue_date ? formatDate(row.issue_date) : '—'} · {row.status_label}
            </span>
          </div>
          <p className="text-secondary-700 [overflow-wrap:anywhere]">{issueItemsSummary(row) || '—'}</p>
          <p className="text-xs text-secondary-600 [overflow-wrap:anywhere]">
            {row.purpose_label}
            {row.reason ? ` · ${row.reason}` : ''}
            {row.rental_number ? ` · ${row.rental_number}` : ''}
          </p>
        </li>
      ))}
    </PagedShell>
  );
};
